"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import type { editor } from "monaco-editor"
import * as Y from "yjs"
import type { Awareness } from "y-protocols/awareness"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import type { AwarenessUserState } from "@/lib/crdt/awareness-user"
import { normalizePath } from "@/lib/compiler/path-utils"
import { resolveEditorFilePath } from "@/lib/editor-store"
import type { PlainAwarenessCursor, WireAwarenessCursor } from "@/lib/crdt/monaco-awareness"

/** Profile/initials tag stays expanded while remote peer is actively editing. */
const TAG_COLLAPSE_MS = 3000
/** Full profile card dismisses this long after hover/click focus is lost. */
const DETAILS_DISMISS_MS = 3000

type RemoteCursor = {
  clientId: number
  user: AwarenessUserState
  left: number
  top: number
  height: number
  /** Signature of caret location — used to detect live edit / cursor activity. */
  activitySig: string
}

type AwarenessSelectionState = {
  selection?: {
    anchor?: unknown
    head?: unknown
  }
  /** JSON-safe cursor payload from monaco-awareness (survives awareness wire). */
  cursor?: PlainAwarenessCursor | WireAwarenessCursor | {
    anchor?: unknown
    head?: unknown
  }
  activeFile?: string | null
  user?: AwarenessUserState & Record<string, unknown>
}

function readSelection(
  state: AwarenessSelectionState,
): { anchor?: unknown; head?: unknown } | null {
  const raw = state.selection
  if (!raw || typeof raw !== "object") return null
  return raw as { anchor?: unknown; head?: unknown }
}

function isWireCursor(
  cursor: AwarenessSelectionState["cursor"],
): cursor is WireAwarenessCursor {
  if (!cursor || typeof cursor !== "object") return false
  const c = cursor as Record<string, unknown>
  const head = c.head
  return head != null && typeof head === "object" && typeof (head as { index?: unknown }).index === "number"
}

function isPlainCursor(
  cursor: AwarenessSelectionState["cursor"],
): cursor is PlainAwarenessCursor {
  if (!cursor || typeof cursor !== "object") return false
  const c = cursor as Record<string, unknown>
  return typeof c.index === "number" && typeof c.line === "number" && typeof c.column === "number"
}

function resolvePlainCursor(
  model: editor.ITextModel,
  normalizedFile: string,
  cursor: PlainAwarenessCursor,
): { lineNumber: number; column: number } | null {
  if (cursor.file && normalizePath(cursor.file) !== normalizedFile) return null
  if (Number.isFinite(cursor.line) && Number.isFinite(cursor.column)) {
    return { lineNumber: cursor.line, column: cursor.column }
  }
  if (Number.isFinite(cursor.index)) {
    const index = Math.max(0, Math.min(cursor.index, model.getValueLength()))
    return model.getPositionAt(index)
  }
  return null
}

function hasResolvableCursor(state: AwarenessSelectionState): boolean {
  if (isPlainCursor(state.cursor) || isWireCursor(state.cursor)) return true
  return Boolean(readSelection(state))
}

function resolveRemoteCursorPosition(
  doc: Y.Doc,
  model: editor.ITextModel,
  normalizedFile: string,
  state: AwarenessSelectionState,
): { lineNumber: number; column: number } | null {
  if (isPlainCursor(state.cursor)) {
    return resolvePlainCursor(model, normalizedFile, state.cursor)
  }

  // Prefer wire cursor over y-monaco selection — RelativePosition in selection breaks after remote edits.
  if (isWireCursor(state.cursor)) {
    return resolveHeadPosition(doc, model, normalizedFile, {
      anchor: state.cursor.anchor,
      head: state.cursor.head,
    })
  }

  const selection = readSelection(state)
  if (!selection) return null
  return resolveHeadPosition(doc, model, normalizedFile, selection)
}

function resolveHeadPosition(
  doc: Y.Doc,
  model: editor.ITextModel,
  normalizedFile: string,
  selection: { anchor?: unknown; head?: unknown },
): { lineNumber: number; column: number } | null {
  const headRaw = selection.head
  if (!headRaw || typeof headRaw !== "object") return null

  // Plain index format (backend guide / legacy): { type: "main.tex", index: 42 }
  const plain = headRaw as { type?: unknown; index?: unknown }
  if (typeof plain.index === "number" && Number.isFinite(plain.index)) {
    if (typeof plain.type === "string" && normalizePath(plain.type) !== normalizedFile) {
      return null
    }
    const index = Math.max(0, Math.min(plain.index, model.getValueLength()))
    return model.getPositionAt(index)
  }

  // Yjs relative position — must rehydrate after awareness JSON round-trip on the wire.
  try {
    const headRel = Y.createRelativePositionFromJSON(
      headRaw as Parameters<typeof Y.createRelativePositionFromJSON>[0],
    )
    const headAbs = Y.createAbsolutePositionFromRelativePosition(headRel, doc)
    if (!headAbs || !(headAbs.type instanceof Y.Text)) return null
    const index = Math.max(0, Math.min(headAbs.index, model.getValueLength()))
    return model.getPositionAt(index)
  } catch {
    return null
  }
}

function readAwarenessUser(raw: unknown): AwarenessUserState | null {
  if (!raw || typeof raw !== "object") return null
  const user = raw as Record<string, unknown>
  const name = typeof user.name === "string" ? user.name : ""
  const color = typeof user.color === "string" ? user.color : "#22c55e"
  if (!name) return null
  return {
    name,
    color,
    colorLight: typeof user.colorLight === "string" ? user.colorLight : "rgba(34, 197, 94, 0.2)",
    initials: typeof user.initials === "string" ? user.initials : name.slice(0, 2).toUpperCase(),
    username: typeof user.username === "string" ? user.username : undefined,
    avatarUrl: typeof user.avatarUrl === "string" ? user.avatarUrl : undefined,
  }
}

function activitySignature(
  clientId: number,
  state: AwarenessSelectionState,
  headPos: { lineNumber: number; column: number },
): string {
  let indexPart = `${headPos.lineNumber}:${headPos.column}`
  if (isWireCursor(state.cursor)) {
    indexPart = `${state.cursor.head.index}`
  } else if (isPlainCursor(state.cursor)) {
    indexPart = `${state.cursor.index}`
  }
  return `${clientId}:${indexPart}`
}

interface CollaboratorCursorsProps {
  editor: editor.IStandaloneCodeEditor | null
  fileId: string
  awareness: Awareness | null
  doc: Y.Doc | null
  ytext: Y.Text | null
}

export function CollaboratorCursors({
  editor,
  fileId,
  awareness,
  doc,
  ytext,
}: CollaboratorCursorsProps) {
  const [cursors, setCursors] = useState<RemoteCursor[]>([])
  const [styleNonce, setStyleNonce] = useState(0)
  /** Forces re-render when expand/collapse timers fire. */
  const [clock, setClock] = useState(0)
  /** Client whose full profile card is open (hover/click). */
  const [detailsClientId, setDetailsClientId] = useState<number | null>(null)
  /** Portal host inside Monaco's DOM — getScrolledVisiblePosition is relative to this node. */
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null)

  const lastActivityAtRef = useRef<Map<number, number>>(new Map())
  const lastActivitySigRef = useRef<Map<number, string>>(new Map())
  const collapseTimerRef = useRef<number | null>(null)
  const detailsDismissTimerRef = useRef<number | null>(null)

  const normalizedFile = useMemo(
    () => normalizePath(resolveEditorFilePath(fileId)),
    [fileId],
  )

  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current != null) {
      window.clearTimeout(collapseTimerRef.current)
      collapseTimerRef.current = null
    }
  }, [])

  const clearDetailsDismissTimer = useCallback(() => {
    if (detailsDismissTimerRef.current != null) {
      window.clearTimeout(detailsDismissTimerRef.current)
      detailsDismissTimerRef.current = null
    }
  }, [])

  const scheduleCollapseTick = useCallback(() => {
    clearCollapseTimer()
    let soonest = Number.POSITIVE_INFINITY
    const now = Date.now()
    lastActivityAtRef.current.forEach((at) => {
      const remaining = at + TAG_COLLAPSE_MS - now
      if (remaining > 0 && remaining < soonest) soonest = remaining
    })
    if (!Number.isFinite(soonest)) return
    collapseTimerRef.current = window.setTimeout(() => {
      collapseTimerRef.current = null
      setClock((n) => n + 1)
      scheduleCollapseTick()
    }, Math.max(50, soonest + 16))
  }, [clearCollapseTimer])

  const openDetails = useCallback((clientId: number) => {
    clearDetailsDismissTimer()
    setDetailsClientId(clientId)
  }, [clearDetailsDismissTimer])

  const scheduleDetailsDismiss = useCallback(() => {
    clearDetailsDismissTimer()
    detailsDismissTimerRef.current = window.setTimeout(() => {
      detailsDismissTimerRef.current = null
      setDetailsClientId(null)
    }, DETAILS_DISMISS_MS)
  }, [clearDetailsDismissTimer])

  const recompute = useCallback(() => {
    if (!editor || !awareness || !doc || !ytext) {
      setCursors([])
      return
    }

    const model = editor.getModel()
    if (!model || model.isDisposed()) {
      setCursors([])
      return
    }

    const next: RemoteCursor[] = []
    const seen = new Set<number>()
    const now = Date.now()

    awareness.getStates().forEach((rawState, clientId) => {
      if (clientId === doc.clientID) return

      const state = rawState as AwarenessSelectionState
      const user = readAwarenessUser(state.user)
      const remoteFile = state.activeFile ? normalizePath(state.activeFile) : normalizedFile
      const headPos = user ? resolveRemoteCursorPosition(doc, model, normalizedFile, state) : null

      if (!user) return
      if (remoteFile && remoteFile !== normalizedFile) return
      if (!hasResolvableCursor(state) || !headPos) return

      const visible = editor.getScrolledVisiblePosition(headPos)
      if (!visible) return

      const activitySig = activitySignature(clientId, state, headPos)
      const prevSig = lastActivitySigRef.current.get(clientId)
      if (prevSig !== activitySig) {
        lastActivitySigRef.current.set(clientId, activitySig)
        // Expand only on real cursor/edit movement — not on first sight of an idle peer.
        if (prevSig != null) {
          lastActivityAtRef.current.set(clientId, now)
        }
      }

      seen.add(clientId)
      // getScrolledVisiblePosition is relative to editor.getDomNode() (includes padding +
      // gutters). Overlay is portaled into that node — do NOT add contentLeft / padding again.
      next.push({
        clientId,
        user,
        left: visible.left,
        top: visible.top,
        height: visible.height,
        activitySig,
      })
    })

    // Drop activity tracking for peers who left this file / room.
    lastActivityAtRef.current.forEach((_, id) => {
      if (!seen.has(id)) {
        lastActivityAtRef.current.delete(id)
        lastActivitySigRef.current.delete(id)
      }
    })
    if (detailsClientId != null && !seen.has(detailsClientId)) {
      setDetailsClientId(null)
    }

    setCursors(next)
    setStyleNonce((n) => n + 1)
    scheduleCollapseTick()
  }, [awareness, detailsClientId, doc, editor, normalizedFile, scheduleCollapseTick, ytext])

  // Mount overlay inside Monaco's root so getScrolledVisiblePosition coords match.
  // A sibling overlay on the React wrapper was painting ~½ line too high.
  useEffect(() => {
    if (!editor) {
      setOverlayHost(null)
      return
    }
    const domNode = editor.getDomNode()
    if (!domNode) {
      setOverlayHost(null)
      return
    }
    if (getComputedStyle(domNode).position === "static") {
      domNode.style.position = "relative"
    }
    setOverlayHost(domNode)
  }, [editor])

  useEffect(() => {
    recompute()
    if (!editor || !awareness) return

    const disposables = [
      editor.onDidChangeCursorPosition(recompute),
      editor.onDidScrollChange(recompute),
      editor.onDidLayoutChange(recompute),
    ]

    const onAwarenessChange = () => recompute()
    awareness.on("change", onAwarenessChange)
    awareness.on("update", onAwarenessChange)

    const layoutObserver = new ResizeObserver(() => recompute())
    const domNode = editor.getDomNode()
    if (domNode) layoutObserver.observe(domNode)

    return () => {
      disposables.forEach((d) => d.dispose())
      awareness.off("change", onAwarenessChange)
      awareness.off("update", onAwarenessChange)
      layoutObserver.disconnect()
      clearCollapseTimer()
      clearDetailsDismissTimer()
    }
  }, [awareness, clearCollapseTimer, clearDetailsDismissTimer, editor, recompute])

  const now = Date.now()
  // clock is only used to invalidate expand/collapse after timers fire
  void clock

  const dynamicStyles = useMemo(() => {
    if (cursors.length === 0) return ""
    return cursors
      .map(({ clientId, user }) => `
        .monaco-editor .yRemoteSelection-${clientId} {
          background-color: ${user.colorLight} !important;
        }
        /* Hide y-monaco's built-in caret — we draw our own I-beam in the portaled overlay. */
        .monaco-editor .yRemoteSelectionHead-${clientId} {
          border-left: none !important;
          border-top: none !important;
          border-bottom: none !important;
        }
      `)
      .join("\n")
  }, [cursors, styleNonce])

  const styleTag = dynamicStyles ? <style>{dynamicStyles}</style> : null

  if (!editor || !overlayHost || cursors.length === 0) {
    return styleTag
  }

  return createPortal(
    <>
      {styleTag}
      <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
        {cursors.map((cursor) => {
          const lastAt = lastActivityAtRef.current.get(cursor.clientId) ?? 0
          const expanded = now - lastAt < TAG_COLLAPSE_MS
          const detailsOpen = detailsClientId === cursor.clientId
          const color = cursor.user.color

          return (
            <div
              key={cursor.clientId}
              className="pointer-events-none absolute"
              style={{
                left: cursor.left,
                top: cursor.top,
                height: cursor.height,
              }}
            >
              {/* I-beam caret (collapsed + expanded) */}
              <div
                className="pointer-events-auto absolute left-0 top-0 w-[2px] cursor-pointer"
                style={{
                  height: cursor.height,
                  backgroundColor: color,
                }}
                onMouseEnter={() => openDetails(cursor.clientId)}
                onMouseLeave={scheduleDetailsDismiss}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openDetails(cursor.clientId)
                }}
                role="button"
                tabIndex={0}
                aria-label={`${cursor.user.name} cursor`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    openDetails(cursor.clientId)
                  }
                }}
              >
                {/* Cap bars for I-beam look */}
                <span
                  className="absolute left-1/2 top-0 h-[2px] w-[6px] -translate-x-1/2 rounded-[1px]"
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
                <span
                  className="absolute bottom-0 left-1/2 h-[2px] w-[6px] -translate-x-1/2 rounded-[1px]"
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
              </div>

              {/* Expanded profile / initials tag — only while peer is actively editing */}
              <div
                className="pointer-events-none absolute flex flex-col items-center transition-all duration-200 ease-out"
                style={{
                  left: 1,
                  bottom: cursor.height + 4,
                  opacity: expanded ? 1 : 0,
                  transform: expanded ? "translateX(-50%) scale(1)" : "translateX(-50%) scale(0.7)",
                  pointerEvents: expanded ? "auto" : "none",
                }}
                onMouseEnter={() => openDetails(cursor.clientId)}
                onMouseLeave={scheduleDetailsDismiss}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openDetails(cursor.clientId)
                }}
              >
                <div
                  className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 bg-white shadow-sm"
                  style={{ borderColor: color }}
                >
                  <Avatar className="h-6 w-6">
                    {cursor.user.avatarUrl ? (
                      <AvatarImage src={cursor.user.avatarUrl} alt={cursor.user.name} />
                    ) : null}
                    <AvatarFallback
                      className="text-[10px] font-semibold text-white"
                      style={{ backgroundColor: color }}
                    >
                      {cursor.user.initials}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className="absolute -bottom-1.5 left-1/2 h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[6px] border-x-transparent"
                    style={{ borderTopColor: color }}
                    aria-hidden
                  />
                </div>
              </div>

              {/* Full profile details — click / hover; dismiss 3s after focus lost */}
              {detailsOpen ? (
                <div
                  className="pointer-events-auto absolute z-30 min-w-[180px] rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-lg transition-opacity duration-150"
                  style={{
                    left: 10,
                    top: -8,
                  }}
                  onMouseEnter={() => openDetails(cursor.clientId)}
                  onMouseLeave={scheduleDetailsDismiss}
                >
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-8 w-8 shrink-0">
                      {cursor.user.avatarUrl ? (
                        <AvatarImage src={cursor.user.avatarUrl} alt={cursor.user.name} />
                      ) : null}
                      <AvatarFallback
                        className="text-[11px] font-semibold text-white"
                        style={{ backgroundColor: color }}
                      >
                        {cursor.user.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold leading-tight text-gray-900">
                        {cursor.user.name}
                      </p>
                      {cursor.user.username ? (
                        <p className="truncate text-[12px] leading-tight text-gray-500">
                          @{cursor.user.username}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className="ml-auto inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                  </div>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </>,
    overlayHost,
  )
}
